import { type ResearchRecord, isPriceFresh } from "@pokifolio/contracts";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { ErrorAlert } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { formatDateTime, formatMoney } from "../search/format";
import { LinkRequests } from "../cards/link-requests";
import { describeAssessment } from "./format";
import { useResearchList } from "./research-queries";

const usd = (amount: string) => formatMoney({ amount, currency: "USD" });

/** The admin's queue: every request waiting for a decision, oldest change first on each page. */
export function ApprovalsPage() {
  const [page, setPage] = useState(1);
  const list = useResearchList({ status: "waiting_approval", page });

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Approvals</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Requests to buy that are waiting for your decision. Open one to approve or reject it.
        </p>
      </div>

      <LinkRequests />
      <h2 className="text-lg font-semibold text-foreground">Requests from search</h2>

      {list.isPending ? (
        <Card>
          <p className="text-sm text-muted-foreground">Loading requests…</p>
        </Card>
      ) : list.isError ? (
        <ErrorAlert error={list.error} />
      ) : list.data.total === 0 ? (
        <Card>
          <p className="text-sm text-muted-foreground">No requests are waiting for a decision.</p>
        </Card>
      ) : (
        <>
          <Card className="overflow-x-auto p-0">
            <table className="w-full min-w-[860px] text-left">
              <caption className="sr-only">Requests waiting for a decision</caption>
              <thead className="text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  {["Card", "Asked for", "Checked price", "Condition", "Sent by", ""].map((label) => (
                    <th key={label} scope="col" className="px-4 py-3 font-medium">
                      {label === "" ? <span className="sr-only">Open</span> : label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {list.data.data.map((record) => (
                  <RequestRow key={record.id} record={record} />
                ))}
              </tbody>
            </table>
          </Card>
          {list.data.total > list.data.pageSize && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={page * list.data.pageSize >= list.data.total}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
              <span>{list.data.total} requests in total</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function RequestRow({ record }: { record: ResearchRecord }) {
  const { card, request, price, assessment } = record;
  return (
    <tr className="border-t border-border align-top text-sm">
      <td className="px-4 py-3">
        <p className="font-medium text-foreground">
          {card.name} {card.number}
        </p>
        <p className="text-xs text-muted-foreground">{record.listing.title}</p>
      </td>
      <td className="px-4 py-3">
        {request === null ? (
          "—"
        ) : (
          <span className="font-semibold text-foreground">{usd(request.maxPriceUsd)}</span>
        )}
        {typeof request?.note === "string" && (
          <p className="text-xs text-muted-foreground">“{request.note}”</p>
        )}
      </td>
      <td className="px-4 py-3">
        {price === null ? (
          <span className="text-muted-foreground">Not checked</span>
        ) : (
          <>
            <span className="text-foreground">{usd(price.totalUsd)}</span>
            {!isPriceFresh(price.confirmedAt) && (
              <p className="text-xs text-amber-800 dark:text-amber-300">
                Checked {formatDateTime(price.confirmedAt)}; may have changed
              </p>
            )}
          </>
        )}
      </td>
      <td className="px-4 py-3 text-foreground">
        {assessment === null ? "—" : describeAssessment(assessment)}
      </td>
      <td className="px-4 py-3">
        <p className="text-foreground">{record.owner.name}</p>
        {request !== null && (
          <p className="text-xs text-muted-foreground">{formatDateTime(request.submittedAt)}</p>
        )}
      </td>
      <td className="px-4 py-3">
        <Link
          to="/research/$researchId"
          params={{ researchId: record.id }}
          aria-label={`Review ${card.name} ${card.number}`}
          className="font-medium text-primary hover:underline"
        >
          Review
        </Link>
      </td>
    </tr>
  );
}
